import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Drawer, DrawerHeader, DrawerContent } from 'fronton-react/drawer';
import { Remote } from '@okapi/remote';
import { RootReducer } from 'store/root-reducer';
import { getArticle, getRemoteTimeout, getRobotintConfig } from 'common/selectors';
import { CALCULATE_QUANTITY_GA_CLOSE } from 'features/ga-events/messages/ga-calculate-quantity-close.pdp';
import {
  getDisplayedName,
  getPrimaryPrice,
  getSecondPrice,
  getProductImages,
  getIsPricesSwapped,
  GO_BACK_KEY,
  CLOSE_MODAL_KEY,
  getCalculatorParameters,
  getActiveTintColor,
  getGtinGtin13Master,
  getProductWeight,
} from 'features/product-info';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { Fallback } from './fallback';
import { Modal, ModalContent } from 'fronton-react';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from 'common/hooks';
import { getLaminateConfiguration } from 'features/configurations';
import { isPossibleToCalculateProjectArea } from '../pdp-calculate-quantity/utils';
import { getServiceIdForCalculatorTint } from 'features/related-services';
import { CalculatorModalProps, CalculatorModalMappedState } from './types';
import { drawer, modalContentClassName, drawerHeader, drawerContent } from './styles';
import { getRobotintOptions } from '../pdp-select-tint-color/hooks/helpers/get-robotint-options.helper';
import { robotint } from '../pdp-select-tint-color/components/robotint';
import { TTintColor } from '../pdp-select-tint-color';
import { ADD_COLOR_TO_SELECTED_COLORS } from '../../ducks/product-tint-service';

export const CalculatorModal: React.FC<CalculatorModalProps> = ({
  isShowed,
  onCloseModal,
  data,
  gaCalculateQuantityClose,
  remoteTimeout,
  robotintConfig,
  setSelectedTintColors,
}) => {
  const { t } = useTranslation();
  const breakpoints = useBreakpoints() || {
    mobile: false,
    smMobile: false,
  };

  const handleSelectColor = (color: Required<TTintColor>) => {
    setSelectedTintColors(color);
    robotint.closeModal();
  };

  const handleOnClose = (elementText: string) => {
    gaCalculateQuantityClose({ elementText });
    onCloseModal();
    if (data.isTintAvailable) {
      const { clientId, secret, robotintLink } = robotintConfig;
      const robotintOptions = getRobotintOptions({
        clientId,
        secret,
        robotintLink,
        colorSelect: ({ COLOR_NAME, HEX, COLLECTION_NAME }) =>
          handleSelectColor({
            COLOR_NAME,
            HEX,
            COLLECTION_NAME,
          }),
      });

      robotint.setOptions(robotintOptions);
    }
  };

  const handleModalClose = (e: React.MouseEvent<HTMLElement>) => {
    if (e.target instanceof HTMLElement && e.target.tagName === 'TINT-DIV') {
      return;
    }
    handleOnClose(t(CLOSE_MODAL_KEY));
  };
  const isMobile = breakpoints?.mobile || breakpoints?.smMobile;

  return isMobile ? (
    <Modal
      modalID="pdp-calculator-modal"
      /* HACK: приходится применить такой хак, т.к. в типах модалки у onClose нет аргументов, но event туда передается */
      onClose={handleModalClose as () => void}
      variant="full-screen"
      show={isShowed}
      overlayClose={false}
    >
      <ModalContent className={modalContentClassName}>
        <Remote
          widget="calculator/root"
          id="pdp-calculator"
          data={data}
          fallback={<Fallback />}
          serverTimeout={remoteTimeout}
        />
      </ModalContent>
    </Modal>
  ) : (
    <Drawer
      breakpoint={1200}
      show={isShowed}
      onClose={() => {
        handleOnClose(t(CLOSE_MODAL_KEY));
      }}
      className={drawer}
      overlayClose={false}
    >
      <DrawerHeader
        sticky
        onClickBack={() => {
          handleOnClose(t(GO_BACK_KEY));
        }}
        title=""
        className={drawerHeader}
      />
      <DrawerContent className={drawerContent}>
        <Remote
          widget="calculator/root"
          id="pdp-calculator"
          data={data}
          fallback={<Fallback />}
          serverTimeout={remoteTimeout}
        />
      </DrawerContent>
    </Drawer>
  );
};

export const mapStateToProps = (state: RootReducer): CalculatorModalMappedState => {
  const images = getProductImages(state);
  const primaryPrice = getPrimaryPrice(state);
  const secondPrice = getSecondPrice(state);
  const additionalAsMain = getIsPricesSwapped(state);
  const remoteTimeout = getRemoteTimeout(state);
  const projectConfiguration = getLaminateConfiguration(state);
  const surfaces = getCalculatorParameters(state);
  const isNecessaryToPassProjectData = isPossibleToCalculateProjectArea(projectConfiguration);
  const serviceIdForTint = getServiceIdForCalculatorTint(state);
  const isTintAvailable = serviceIdForTint !== null;
  const activeTintColor = getActiveTintColor(state);
  const barcode = getGtinGtin13Master(state);
  const weight = getProductWeight(state);
  const robotintConfig = getRobotintConfig(state);

  const data = {
    productId: getArticle(state),
    displayedName: getDisplayedName(state) || null,
    regionId: getRegionId(state) || null,
    photo: images?.length ? images[0].url : null,
    mainPrice: primaryPrice?.price || null,
    additionalPrice: secondPrice?.price || null,
    mainUomRus: primaryPrice?.unit || null,
    additionalUomRus: secondPrice?.unit || null,
    additionalAsMain,
    projectConfigurationParameters: isNecessaryToPassProjectData ? projectConfiguration : null,
    surfaces: isNecessaryToPassProjectData ? surfaces : null,
    isTintAvailable,
    collectionName: activeTintColor?.COLLECTION_NAME ?? null,
    colorName: activeTintColor?.COLOR_NAME ?? null,
    hex: activeTintColor?.HEX ?? null,
    barcode: barcode ?? null,
    weight,
    serviceId: serviceIdForTint,
  };

  return {
    data,
    remoteTimeout,
    robotintConfig,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  gaCalculateQuantityClose: ({ elementText }: { elementText: string }) => {
    dispatch(CALCULATE_QUANTITY_GA_CLOSE({ elementText }));
  },
  setSelectedTintColors: (newSelectedColor: TTintColor) =>
    dispatch(ADD_COLOR_TO_SELECTED_COLORS(newSelectedColor)),
});

export const PdpCalculatorModal = connect(mapStateToProps, mapDispatchToProps)(CalculatorModal);
